import React, { useState, useEffect, useRef } from 'react';
import Card from '../../components/common/Card';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import RichTextEditor from '../../components/common/RichTextEditor';
import configService from '../../services/configService';

const normalizeEnabled = (value) => {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return !['false', '0', 'off', 'no'].includes(normalized);
  }
  return Boolean(value);
};

const EmailTemplatesView = () => {
  const bodyEditorRef = useRef(null);
  const lastFocusedField = useRef('body');
  const subjectCursorPos = useRef(null);
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState('');

  const [templateData, setTemplateData] = useState({});
  const [templateDefaults, setTemplateDefaults] = useState({});

  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error' | null

  const selected = templates.find((t) => t.id === selectedId);

  const applyTemplates = (inputTemplates) => {
    const defaults = {};
    const formData = {};

    inputTemplates.forEach((template) => {
      const enabled = normalizeEnabled(template.enabled);
      defaults[template.id] = { enabled, subject: template.subject, body: template.body };
      formData[template.id] = { enabled, subject: template.subject, body: template.body, loaded: true };
    });

    setTemplates(inputTemplates);
    setTemplateDefaults(defaults);
    setTemplateData(formData);
    setSelectedId((prev) => {
      if (prev && inputTemplates.some((t) => t.id === prev)) return prev;
      return inputTemplates[0]?.id || '';
    });
  };

  const loadTemplates = async () => {
    const result = await configService.getEmailTemplates();
    if (!result.success) {
      setTemplates([]);
      return;
    }
    const normalized = (result.data || []).map((item) => ({
      id: String(item.id || item.template_id || item.key || '').trim(),
      name: item.name || item.template_name || item.key,
      description: item.description || '',
      enabled: normalizeEnabled(item.is_enabled ?? item.enabled ?? item.active),
      subject: item.subject || '',
      body: item.body || '',
      variables: Array.isArray(item.variables) ? item.variables : []
    })).filter(t => t.id);
    applyTemplates(normalized);
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const current = templateData[selectedId] || {
    enabled: selected?.enabled ?? true,
    subject: selected?.subject || '',
    body: selected?.body || '',
    loaded: false
  };

  const setField = (field, value) => {
    setTemplateData(prev => ({
      ...prev,
      [selectedId]: { ...prev[selectedId], [field]: value }
    }));
    setSaveStatus(null);
  };

  const handleResetCurrent = () => {
    if (!selectedId || !templateDefaults[selectedId]) return;
    setField('enabled', templateDefaults[selectedId].enabled);
    setField('subject', templateDefaults[selectedId].subject);
    setField('body', templateDefaults[selectedId].body);
  };

  const handleResetAllToDefaults = async () => {
    if (!window.confirm("This will overwrite ALL email templates with their system default subjects and bodies. Continue?")) return;
    setIsResetting(true);
    const result = await configService.resetEmailTemplatesToDefaults();
    if (result.success) {
      await loadTemplates();
      setSaveStatus('success');
    } else {
      setSaveStatus('error');
    }
    setIsResetting(false);
  };

  const handleSave = async () => {
    if (!selected) return;
    setIsSaving(true);
    setSaveStatus(null);
    const result = await configService.saveEmailTemplate(selectedId, {
      enabled: current.enabled,
      subject: current.subject,
      body: current.body
    });
    if (result.success) {
      setTemplateDefaults(prev => ({ ...prev, [selectedId]: { ...current } }));
      setSaveStatus('success');
    } else {
      setSaveStatus('error');
    }
    setIsSaving(false);
  };

  const insertVariable = (token) => {
    if (lastFocusedField.current === 'subject') {
      const pos = subjectCursorPos.current;
      const s = current.subject || '';
      if (pos != null) {
        const next = s.slice(0, pos.start) + token + s.slice(pos.end);
        setField('subject', next);
        subjectCursorPos.current = { start: pos.start + token.length, end: pos.start + token.length };
      } else {
        setField('subject', s + token);
      }
    } else {
      const inserted = bodyEditorRef.current?.insertTextAtCursor?.(token);
      if (!inserted) {
        const nextBody = (current.body || '').trim() ? `${current.body}<p>${token}</p>` : `<p>${token}</p>`;
        setField('body', nextBody);
      }
    }
  };

  // Styles
  const pageStyle = { display: 'grid', gridTemplateColumns: '280px 1fr', gap: '24px', alignItems: 'start' };
  const listItemStyle = (active) => ({
    padding: '12px 14px', borderRadius: '8px', cursor: 'pointer',
    background: active ? 'var(--primary)' : 'transparent', color: active ? 'white' : 'var(--text-primary)',
    border: 'none', fontSize: '13px', fontWeight: active ? '600' : '500', transition: 'all 0.15s ease',
    textAlign: 'left', width: '100%', marginBottom: '4px'
  });
  const variablePillStyle = {
    display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px',
    background: 'var(--surface-light, #f8f9fa)', border: '1px solid var(--border)',
    borderRadius: '6px', fontSize: '12px', fontFamily: 'monospace', color: 'var(--primary)',
    cursor: 'pointer', transition: 'all 0.1s ease', marginBottom: '8px', marginRight: '8px'
  };

  return (
    <div style={pageStyle}>
      <aside>
        <Card padding="16px">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Templates
            </span>
          </div>
          <div style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto', paddingRight: '4px' }}>
            {templates.map(t => (
              <button key={t.id} style={listItemStyle(t.id === selectedId)} onClick={() => setSelectedId(t.id)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{t.name}</span>
                  {!templateData[t.id]?.enabled && (
                    <span style={{ fontSize: '10px', background: 'rgba(239,68,68,0.1)', color: '#dc2626', padding: '1px 5px', borderRadius: '4px' }}>Off</span>
                  )}
                </div>
              </button>
            ))}
          </div>
          <hr style={{ margin: '16px 0', border: 'none', borderTop: '1px solid var(--border)' }} />
          <Button variant="secondary" size="small" fullWidth onClick={handleResetAllToDefaults} disabled={isResetting}>
            {isResetting ? 'Resetting...' : 'Seed System Defaults'}
          </Button>
        </Card>
      </aside>

      <main style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {selected ? (
          <>
            <div>
              <h2 style={{ fontSize: '22px', fontWeight: '800', color: 'var(--text-primary)', margin: '0 0 4px 0' }}>{selected.name}</h2>
              <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: 0 }}>{selected.description}</p>
            </div>

            <Card padding="16px">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600' }}>Email Status</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Toggle this to enable/disable automated emails for this event.</div>
                </div>
                <Button variant={current.enabled ? 'primary' : 'secondary'} size="small" onClick={() => setField('enabled', !current.enabled)}>
                  {current.enabled ? 'ACTIVE' : 'DISABLED'}
                </Button>
              </div>
            </Card>

            <Card padding="16px">
              <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', display: 'block', marginBottom: '12px' }}>AVAILABLE VARIABLES</span>
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {selected.variables.map(v => (
                  <button key={v.token} style={variablePillStyle} onClick={() => insertVariable(v.token)} title={v.description}>
                    <strong>{v.token}</strong> <span style={{ opacity: 0.6, marginLeft: '4px' }}>— {v.description}</span>
                  </button>
                ))}
              </div>
            </Card>

            <Card padding="20px">
              <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>SUBJECT LINE</span>
              <Input
                value={current.subject}
                onChange={(e) => setField('subject', e.target.value)}
                onFocus={() => { lastFocusedField.current = 'subject'; }}
                onBlur={(e) => { subjectCursorPos.current = { start: e.target.selectionStart, end: e.target.selectionEnd }; }}
                placeholder="Enter subject..."
              />
            </Card>

            <Card padding="20px">
              <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>EMAIL BODY (HTML)</span>
              <RichTextEditor
                ref={bodyEditorRef}
                value={current.body}
                onChange={(html) => setField('body', html)}
                onFocus={() => { lastFocusedField.current = 'body'; }}
              />
            </Card>

            {saveStatus && (
              <div style={{
                padding: '12px', borderRadius: '8px', fontSize: '14px', textAlign: 'center',
                background: saveStatus === 'success' ? '#ecfdf5' : '#fef2f2',
                color: saveStatus === 'success' ? '#059669' : '#dc2626',
                border: `1px solid ${saveStatus === 'success' ? '#10b981' : '#ef4444'}`
              }}>
                {saveStatus === 'success' ? 'Changes saved successfully!' : 'Error saving template. Please check backend logs.'}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <Button variant="primary" onClick={handleSave} disabled={isSaving || !current.loaded}>
                {isSaving ? 'Saving...' : 'Save Template'}
              </Button>
              <Button variant="secondary" onClick={handleResetCurrent} disabled={isSaving}>
                Discard Changes
              </Button>
            </div>
          </>
        ) : (
          <Card padding="40px">
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
              Select a template from the left to start editing, or click <strong>Seed System Defaults</strong> to initialize the system.
            </div>
          </Card>
        )}
      </main>
    </div>
  );
};

export default EmailTemplatesView;
